import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import { type SnackbarState, useSnackbar } from '~/components/core/Molecules/Snackbar';

import { instantOf } from '../ActionHistory/utils';
import type { ActivityRow } from '../ActivityPanel/types';
import { LOG_LOOKUP_NOTICE, LOG_LOOKUP_SLACK_MINUTES } from '../constants';
import type { StageChapter } from '../LogPanel/StagesRail/types';
import { chapterEntryId, cliveEntryId } from '../LogPanel/StagesRail/utils';
import { isLogEntryDrawn, scrollToLogEntry } from '../utils';

/** A read made for a card's entry or a chapter's, once it has landed. A fresh object per read, so each is resolved once. */
type LandedRead = { kind: 'card'; rowKey: string } | { kind: 'chapter'; chapter: StageChapter };

interface UseSelectLogEntryReturn {
  /** The entry the activity panel last took the reader to, which stays marked until another is. */
  selectedEventId?: string;
  /** Brings a card's log entry into view and marks it selected, reading the entry in first where it is not loaded. */
  selectLogEntry: (row: ActivityRow) => void;
  /** Unmarks the entry, unless another has been selected since. */
  deselectLogEntry: (eventId: string) => void;
  /**
   * Puts a stage change's entry at the top of the log, reading the log in first where it does not reach back to the
   * move. Marks nothing: the reader is taken to a place in the log, not to a card.
   */
  selectChapter: (chapter: StageChapter) => void;
  /** Of the entries the log draws, the one `selectChapter` takes the reader to without a read. */
  chapterEntryIdInLog: (chapter: StageChapter) => string | undefined;
  /** The chapter `selectChapter` is reading the log in for, by its key, until the read lands or another click comes. */
  pendingChapterKey?: string;
  /** What to tell the reader when the entry cannot be shown, for a `FeedbackSnackbar`. */
  notice: SnackbarState | null;
  closeNotice: () => void;
}

/**
 * Takes the reader from a card, or from a chapter of the stages rail, to its entry in the log.
 *
 * The cards come from every row the claim has and the chapters from every stage change, and the log holds only what has
 * loaded, so an old card's entry, or the first entry after an old move, can be older than the log's window. Then one
 * read brings in everything from just before the row was saved, or the claim moved, up to the window, and the entry is
 * looked up again in what that read produced, once it has rendered.
 *
 * The last click wins: a read that lands after the reader has picked another card or chapter changes nothing. While a
 * chapter's read is out, the chapter is pending, so the rail can mark it and the log wait at its oldest end.
 */
export const useSelectLogEntry = (
  /** The rows as they are now, which is what a read is resolved against when it lands. */
  rows: ActivityRow[],
  /** The loaded entries as they are now, oldest first. */
  notifications: StpNotification[],
  /** Whether there is history before the first loaded entry that has not been read. */
  hasEarlier: boolean,
  /** Reads every entry from an ISO-8601 bound up to the loaded ones. */
  loadSince: (since: string) => Promise<void>
): UseSelectLogEntryReturn => {
  const [selectedEventId, setSelectedEventId] = useState<string>();
  const [landedRead, setLandedRead] = useState<LandedRead | null>(null);
  const [pendingChapterKey, setPendingChapterKey] = useState<string>();
  // A ref and not a reset of `landedRead`: the effect below re-runs whenever the rows change, and a
  // render can come between it and that reset landing.
  const resolvedReadRef = useRef<LandedRead | null>(null);
  const lastClickRef = useRef(0);
  const { snackbarState: notice, showSnackbar, closeSnackbar: closeNotice } = useSnackbar();

  const select = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    scrollToLogEntry(eventId);
  }, []);

  const goToChapterEntry = useCallback(
    (eventId: string | undefined) => {
      if (eventId) {
        scrollToLogEntry(eventId, 'start');
      } else {
        showSnackbar(LOG_LOOKUP_NOTICE.chapterNotInLog, 'info');
      }
    },
    [showSnackbar]
  );

  // Conditional: picking another card is also a click outside the current one, and must not undo it.
  const deselectLogEntry = useCallback((eventId: string) => {
    setSelectedEventId((current) => (current === eventId ? undefined : current));
  }, []);

  /** Counts a click on a card or a chapter, and returns whether it is still the last one. */
  const countClick = useCallback((): (() => boolean) => {
    lastClickRef.current += 1;
    const click = lastClickRef.current;
    setPendingChapterKey(undefined);

    return () => click === lastClickRef.current;
  }, []);

  /** Reads from just before `at` up to the loaded entries, then resolves `read` unless a click has come since. */
  const readThenResolve = useCallback(
    (at: moment.MomentInput, read: LandedRead, isLastClick: () => boolean, failure: string) => {
      const since = moment.utc(at).subtract(LOG_LOOKUP_SLACK_MINUTES, 'minutes').toISOString();
      loadSince(since).then(
        () => {
          if (isLastClick()) setLandedRead(read);
        },
        () => {
          if (!isLastClick()) return;
          setPendingChapterKey(undefined);
          showSnackbar(failure, 'error');
        }
      );
    },
    [loadSince, showSnackbar]
  );

  const selectLogEntry = useCallback(
    (row: ActivityRow) => {
      const isLastClick = countClick();

      if (row.logEventId) {
        select(row.logEventId);

        return;
      }

      const createdAt = row.kind === 'recommendation' ? row.action.created_at : row.ask.created_at;
      readThenResolve(createdAt, { kind: 'card', rowKey: row.key }, isLastClick, LOG_LOOKUP_NOTICE.failed);
    },
    [countClick, readThenResolve, select]
  );

  // Until the log reaches back to the move, the first entry it holds after the move need not be the first there is,
  // so only Clive's own entry about the move is gone to without a read.
  const reachesMove = useCallback(
    (chapter: StageChapter): boolean =>
      !hasEarlier || (notifications.length > 0 && instantOf(notifications[0].timestamp) <= chapter.changedAt),
    [hasEarlier, notifications]
  );

  const chapterEntryIdInLog = useCallback(
    (chapter: StageChapter): string | undefined =>
      reachesMove(chapter)
        ? chapterEntryId(chapter, rows, notifications, isLogEntryDrawn)
        : cliveEntryId(chapter, rows, notifications, isLogEntryDrawn),
    [reachesMove, rows, notifications]
  );

  const selectChapter = useCallback(
    (chapter: StageChapter) => {
      const isLastClick = countClick();
      const eventId = chapterEntryIdInLog(chapter);

      if (eventId || reachesMove(chapter)) {
        goToChapterEntry(eventId);

        return;
      }

      setPendingChapterKey(chapter.key);
      readThenResolve(chapter.changedAt, { kind: 'chapter', chapter }, isLastClick, LOG_LOOKUP_NOTICE.chapterFailed);
    },
    [countClick, chapterEntryIdInLog, reachesMove, goToChapterEntry, readThenResolve]
  );

  // The read's events were set before it resolved, so the render that carries `landedRead` carries
  // them too, and the entry is in the DOM by the time this runs. Before paint, and after the log's own
  // layout effects, so the landing scroll is the one that stands over the log holding the reader's place,
  // and the rail never draws the frame between the read landing and the chapter no longer pending.
  useLayoutEffect(() => {
    if (!landedRead || resolvedReadRef.current === landedRead) return;
    resolvedReadRef.current = landedRead;

    if (landedRead.kind === 'chapter') {
      goToChapterEntry(chapterEntryId(landedRead.chapter, rows, notifications, isLogEntryDrawn));
      setPendingChapterKey(undefined);

      return;
    }

    const logEventId = rows.find(({ key }) => key === landedRead.rowKey)?.logEventId;
    if (logEventId) {
      select(logEventId);
    } else {
      showSnackbar(LOG_LOOKUP_NOTICE.notInLog, 'info');
    }
  }, [landedRead, rows, notifications, select, goToChapterEntry, showSnackbar]);

  return {
    selectedEventId,
    selectLogEntry,
    deselectLogEntry,
    selectChapter,
    chapterEntryIdInLog,
    pendingChapterKey,
    notice,
    closeNotice,
  };
};
